import React, { useEffect, useState } from 'react';

import DataGrid from './DataGrid';
import { DataGridColDef } from './DataGrid/DataGrid';
import { toCurrency, toRate } from '../utils/utils';
import AccountingEntryModal from '../pages/Accounting/Components/AccountingEntryModal';
import { AssetFutureValueResponse, IrvSchedule } from 'services/assets';
import DF from 'DisplayFramework';
import { Tabs } from 'antd';
import GetLabel from 'DisplayFramework/components/GetLabel';
import { Line } from '@ant-design/plots';
import _, { isEmpty } from 'lodash';

export type IrvScheduleTableProps = {
  irvSchedule: IrvSchedule[];
  currcode: string;
  listContextFV?: AssetFutureValueResponse[];
};
const { TabPane } = Tabs;

const IrvScheduleTable = (props: IrvScheduleTableProps) => {
  const { irvSchedule: irvprops, currcode, listContextFV } = props;

  const [irvSchedule, setirvSchedule] = useState([]);
  const [ChartData, setChartData] = useState<any>([]);

  useEffect(() => {
    let newirvschedule: any = irvprops;
    const allFVSchedule = listContextFV?.map((ctx) =>
      _.pick(
        {
          fvSchedule: ctx.fvSchedule,
          contextcodelabel:
            ctx.contextcodelabel +
            ' - ' +
            ctx.fvtypelabel +
            ' - ' +
            ctx.duration +
            ' (M)',
        },
        'fvSchedule',
        'contextcodelabel'
      )
    );
    if (!isEmpty(listContextFV)) {
      allFVSchedule?.forEach((ctx, i) => {
        newirvschedule = _.values(
          _.merge(
            _.keyBy(newirvschedule, 'enddate'),
            _.keyBy(
              ctx.fvSchedule.map((assetfv) => ({
                [`${ctx.contextcodelabel}:amount`]: assetfv.fvamount,
                [`${ctx.contextcodelabel}:percentage`]: assetfv.fvpercentage,
                ...assetfv,
              })),
              'fvdate'
            )
          )
        );
      });
    }

    //prepare chart data
    let newdata = irvprops
      .map((irv: any) => ({
        enddate: irv.enddate,
        irvtype: 'irvamount',
        amount: Number(irv.irvamount.toFixed(0)),
      }))
      .concat(
        irvprops.map((irv: any) => ({
          enddate: irv.enddate,
          irvtype: 'finrvamount',
          amount: Number(irv.finrvamount.toFixed(0)),
        }))
      );
    if (!isEmpty(allFVSchedule)) {
      allFVSchedule?.forEach((ctx) => {
        newdata = newdata.concat(
          ctx.fvSchedule.map((fvsch) => ({
            enddate: fvsch.fvdate,
            irvtype: ctx.contextcodelabel,
            amount: Number(fvsch.fvamount.toFixed(0)),
          }))
        );
      });
    }
    setChartData(newdata);
    setirvSchedule(newirvschedule);
  }, [irvprops, listContextFV]);

  const config = {
    data: ChartData,
    xField: 'enddate',
    yField: 'amount',
    seriesField: 'irvtype',
    xAxis: {
      type: 'time',
    },
    yAxis: {
      label: {
        formatter: (v: any) => toCurrency(v, currcode),
      },
    },
  };

  const COLUMNS: DataGridColDef<any>[] = [
    {
      comcode: 'irvorder',
      dataIndex: 'irvorder',
      fixed: 'left',
      width: 80,
    },

    {
      comcode: 'ctstype',
      dataIndex: 'ctstype',
      fixed: 'left',
      width: 100,
    },
    {
      comcode: 'duedate',
      dataIndex: 'enddate',
      fixed: 'left',
      width: 100,
    },

    {
      align: 'right',
      comcode: 'rvpercentage',
      dataIndex: 'rvpercentage',
      render: toRate,
      width: 100,
    },
    {
      align: 'right',
      comcode: 'irvamount',
      dataIndex: 'irvamount',
      render: (val) => toCurrency(val, currcode ?? localStorage.getItem('currcode') ?? ''),
      width: 150,
    },
    {
      align: 'right',
      comcode: 'finrvamount',
      dataIndex: 'finrvamount',
      render: (val) => toCurrency(val, currcode ?? localStorage.getItem('currcode') ?? ''),
      width: 150,
    },
    {
      align: 'right',
      comcode: 'rentalpart',
      dataIndex: 'rentalpart',
      render: (val) => toCurrency(val, currcode ?? localStorage.getItem('currcode') ?? ''),
      width: 150,
    },
    {
      align: 'right',
      comcode: 'interestpart',
      dataIndex: 'interestpart',
      render: (val) => toCurrency(val, currcode ?? localStorage.getItem('currcode') ?? ''),
      width: 150,
    },
    {
      align: 'right',
      comcode: 'capitalpart',
      dataIndex: 'capitalpart',
      render: (val) => toCurrency(val, currcode ?? localStorage.getItem('currcode') ?? ''),
      width: 150,
    },
    {
      comcode: 'rvgap',
      dataIndex: 'rvgap',
      render: (val) => toCurrency(val, currcode ?? localStorage.getItem('currcode') ?? ''),
      width: 150,
    },
    {
      comcode: 'finrvwithtrg',
      dataIndex: 'finrvwithtrg',
      render: (val) => toCurrency(val, currcode ?? localStorage.getItem('currcode') ?? ''),
      width: 150,
    },

    {
      comcode: 'rvgapwithtrg',
      dataIndex: 'rvgapwithtrg',
      render: (val) => toCurrency(val, currcode ?? localStorage.getItem('currcode') ?? ''),
      width: 150,
    },
    {
      comcode: 'trg',
      dataIndex: 'trg',
      render: toRate,
      width: 100,
    },

    {
      comcode: 'accountingdate',
      dataIndex: 'accountingdate',
      width: 100,
    },
    {
      comcode: 'accountingreference',
      dataIndex: 'accountingreference',
      render: (accountingreference) => (
        <AccountingEntryModal accountingReference={accountingreference} />
      ),
      width: 200,
      fixed: 'right',
    },
  ];
  const getColumns = (): DataGridColDef<any>[] => {
    let newColumns: DataGridColDef<any>[] = [];
    const allFVSchedule = listContextFV?.map((ctx) =>
      _.pick(
        {
          contextcodelabel:
            ctx.contextcodelabel +
            ' - ' +
            ctx.fvtypelabel +
            ' - ' +
            ctx.duration +
            ' (M)',
        },
        'contextcodelabel'
      )
    );
    allFVSchedule?.forEach((ctx) => {
      newColumns.push({
        title: `${ctx.contextcodelabel}:percentage`,
        dataIndex: `${ctx.contextcodelabel}:percentage`,
        align: 'right',

        render: toRate,
        width: 150,
      });
      newColumns.push({
        title: `${ctx.contextcodelabel}:amount`,
        dataIndex: `${ctx.contextcodelabel}:amount`,
        align: 'right',

        render: (val: any) => toCurrency(val, currcode ?? localStorage.getItem('currcode') ?? ''),
        width: 150,
      });
    });
    if (!isEmpty(newColumns)) {
      newColumns = [
        ...COLUMNS.slice(0, 5),
        ...newColumns,
        ...COLUMNS.slice(newColumns.length + 1, COLUMNS.length),
      ];
    } else newColumns = COLUMNS;

    return newColumns;
  };

  return (
    <DF.Provider pagcode="IrvSchedule">
      <Tabs defaultActiveKey="1" animated>
        <TabPane tab={<GetLabel label="Table" />} key="1">
          <DataGrid
            dataSource={irvSchedule}
            columns={getColumns()}
            rowKey={(record) => record.irvorder}
            scroll={{ x: 1000, y: 360 }}
            pagination={{ pageSize: 200 }}
          />
        </TabPane>
        <TabPane tab={<GetLabel label="Charts" />} key="2">
          {!isEmpty(ChartData) && <Line {...config} />}
        </TabPane>
      </Tabs>
    </DF.Provider>
  );
};

export default IrvScheduleTable;
